import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  ArrowRight, RotateCcw, Activity, Shield, Server,
  GitCompare, AlertTriangle, ChevronLeft,
  ChevronRight, Wifi, Globe, FileSearch, Package,
  ServerCrash, ExternalLink
} from 'lucide-react';
import PageContainer from '../components/Layout/PageContainer';
import Card from '../components/UI/Card';
import Button from '../components/UI/Button';
import Badge from '../components/UI/Badge';

// ====================================================================
// MOCK DATA (hardcoded — exact values from the README)
// ====================================================================

const HEARTBEAT_PAYLOAD = {
  server_id: 'srv-001a2b3c4d5e',
  timestamp: '2026-05-25T10:30:00+00:00',
  connections: [
    { ip: '185.220.101.5', port: 443 },
    { ip: '10.0.0.1', port: 8080 },
    { ip: '142.250.80.46', port: 443 },
  ],
  dns: ['google.com', 'evil-phishing-site.xyz', 'github.com'],
  process_hashes: [
    { path: '/usr/bin/sshd', sha256: 'e3b0c44298fc1c14...' },
    { path: '/usr/sbin/nginx', sha256: 'd7a8fbb307d78094...' },
    { path: '/tmp/malicious.bin', sha256: 'a1b2c3d4e5f6...' },
  ],
};

const SNAPSHOT_PAYLOAD = {
  server_id: 'srv-001a2b3c4d5e',
  timestamp: '2026-05-25T11:00:00+00:00',
  packages: [
    { name: 'openssl', version: '3.0.2', ecosystem: 'apt' },
    { name: 'nginx', version: '1.24.0', ecosystem: 'apt' },
    { name: 'postgresql', version: '15.4', ecosystem: 'apt' },
    { name: 'requests', version: '2.31.0', ecosystem: 'pip' },
    { name: 'flask', version: '3.0.0', ecosystem: 'pip' },
    { name: 'log4j', version: '2.14.1', ecosystem: 'apt' },
  ],
  services: ['sshd', 'nginx', 'postgresql', 'docker'],
  ports: [
    { port: 22, protocol: 'tcp', service: 'sshd' },
    { port: 80, protocol: 'tcp', service: 'nginx' },
    { port: 443, protocol: 'tcp', service: 'nginx' },
    { port: 5432, protocol: 'tcp', service: 'postgresql' },
    { port: 8080, protocol: 'tcp', service: 'docker' },
  ],
  system: {
    os: 'Linux 6.5.0-generic',
    hostname: 'prod-server-01',
    cpu: { cores: 4, percent: 12.5 },
    memory: { total_bytes: 8589934592, available_bytes: 4294967296 },
  },
};

const PREVIOUS_SNAPSHOT = {
  nginx: '1.22.0',
  openssl: '3.0.2',
  postgresql: '15.4',
  requests: '2.31.0',
};

const CURRENT_SNAPSHOT = {
  nginx: '1.24.0',
  openssl: '3.0.2',
  postgresql: '15.4',
  requests: '2.31.0',
  flask: '3.0.0',
};

const CVE_ALERTS = [
  {
    id: 'CVE-2022-0778',
    severity: 'CRITICAL',
    cvss: 9.1,
    package: 'openssl',
    version: '3.0.2',
    ecosystem: 'apt',
    description: 'Buffer overread on asymmetric RSA key',
  },
  {
    id: 'CVE-2021-44228',
    severity: 'CRITICAL',
    cvss: 10.0,
    package: 'log4j',
    version: '2.14.1',
    ecosystem: 'apt',
    description: 'JNDI lookup RCE (Log4Shell)',
  },
  {
    id: 'CVE-2023-32681',
    severity: 'MEDIUM',
    cvss: 6.1,
    package: 'requests',
    version: '2.31.0',
    ecosystem: 'pip',
    description: 'Cert verification bypass via redirect',
  },
];

const IOC_ALERTS = [
  {
    type: 'ip',
    value: '185.220.101.5',
    threatLevel: 'HIGH',
    technique: 'T1071.001',
    description: 'Outbound C2 traffic to known Emotet command server',
  },
  {
    type: 'domain',
    value: 'evil-phishing-site.xyz',
    threatLevel: 'MEDIUM',
    technique: 'T1566.002',
    description: 'Spear-phishing link to credential harvesting page',
  },
  {
    type: 'hash',
    value: 'a1b2c3d4e5f6...',
    threatLevel: 'HIGH',
    technique: 'T1055.001',
    description: 'Process injection via known malicious binary',
  },
];

// ====================================================================
// Helper sub-components
// ====================================================================

const CodeBlock = ({ data }) => (
  <pre className="bg-dark-900 rounded-lg p-4 text-xs font-mono text-gray-300 overflow-x-auto leading-relaxed border border-dark-700/50 whitespace-pre-wrap">
    {JSON.stringify(data, null, 2)}
  </pre>
);

const StatCard = ({ icon: Icon, label, value, sublabel }) => (
  <Card className="p-4 flex items-start gap-3">
    <div className="p-2 rounded-lg bg-primary/10 text-primary shrink-0">
      <Icon size={20} />
    </div>
    <div className="min-w-0">
      <p className="text-2xl font-bold text-white">{value}</p>
      <p className="text-sm text-gray-400">{label}</p>
      {sublabel && <p className="text-xs text-gray-500 mt-0.5">{sublabel}</p>}
    </div>
  </Card>
);

const FlowArrow = () => (
  <span className="text-gray-500 mx-2 flex items-center gap-1">
    <span className="border-t border-dashed border-gray-600 w-6 hidden sm:inline-block" />
    <ArrowRight size={14} className="shrink-0" />
  </span>
);

// ====================================================================
// Main Component
// ====================================================================

const EnrichmentFlowDemo = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [stepTransition, setStepTransition] = useState('opacity-100 translate-x-0');
  const stepRef = useRef(currentStep);
  stepRef.current = currentStep;

  // --- Animation on step change ---
  useEffect(() => {
    setStepTransition('opacity-0 translate-x-4');
    const id = requestAnimationFrame(() => {
      setStepTransition('opacity-100 translate-x-0');
    });
    return () => cancelAnimationFrame(id);
  }, [currentStep]);

  // --- Keyboard handler (Enter / Space) ---
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        const el = e.target;
        // Don't interfere with inputs / buttons
        if (
          el.tagName === 'INPUT' ||
          el.tagName === 'TEXTAREA' ||
          el.tagName === 'BUTTON'
        ) {
          return;
        }
        e.preventDefault();
        if (stepRef.current < 6) {
          setCurrentStep((p) => Math.min(p + 1, 6));
        } else {
          setCurrentStep(1);
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // --- Navigation handlers ---
  const goToStep = useCallback((step) => {
    if (step >= 1 && step <= 6) setCurrentStep(step);
  }, []);

  const handleNext = useCallback(() => {
    if (currentStep < 6) {
      setCurrentStep((p) => p + 1);
    } else {
      setCurrentStep(1);
    }
  }, [currentStep]);

  const handlePrev = useCallback(() => {
    setCurrentStep((p) => Math.max(p - 1, 1));
  }, [currentStep]);

  // ==================================================================
  // STEP RENDERERS
  // ==================================================================

  const renderStep1 = () => (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-white">
          Step 1: Agent Heartbeat Received
        </h2>
        <p className="text-sm text-gray-400 mt-1">
          Every 15 minutes, the agent sends lightweight telemetry to the Cloudflare Worker
        </p>
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left — JSON payload */}
        <Card>
          <Card.Header>
            <div className="flex items-center gap-2">
              <Activity size={16} className="text-primary" />
              <span className="text-sm font-medium text-white">Heartbeat Payload</span>
            </div>
          </Card.Header>
          <Card.Body className="p-0">
            <CodeBlock data={HEARTBEAT_PAYLOAD} />
          </Card.Body>
        </Card>

        {/* Right — Summary cards */}
        <div className="space-y-4">
          <StatCard
            icon={Wifi}
            value="3"
            label="Connections Detected"
            sublabel="2 external, 1 internal"
          />
          <StatCard
            icon={Globe}
            value="3"
            label="DNS Queries"
            sublabel="1 flagged domain"
          />
          <StatCard
            icon={FileSearch}
            value="3"
            label="Process Hashes"
            sublabel="1 suspicious binary"
          />
        </div>
      </div>

      {/* Agent → Worker flow diagram */}
      <Card>
        <Card.Body>
          <div className="flex flex-wrap items-center justify-center gap-2 text-sm">
            <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-dark-900 border border-gray-700">
              <Server size={16} className="text-emerald-400" />
              <span className="text-gray-300 font-mono text-xs">Agent on Asset</span>
            </div>
            <FlowArrow />
            <span className="text-xs font-mono text-gray-500 bg-gray-800 px-3 py-1 rounded-full border border-gray-700">
              POST /api/heartbeat
            </span>
            <FlowArrow />
            <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-dark-900 border border-gray-700">
              <ServerCrash size={16} className="text-blue-400" />
              <span className="text-gray-300 font-mono text-xs">Cloudflare Worker</span>
            </div>
          </div>
        </Card.Body>
      </Card>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-white">
          Step 2: TI Feed Enrichment — Real-Time IoC Detection
        </h2>
        <p className="text-sm text-gray-400 mt-1">
          Worker looks up each observable in the local threat indicators database (TI_DB), populated daily from open-source feeds
        </p>
      </div>

      {/* Pipeline flow */}
      <Card>
        <Card.Body>
          <div className="flex flex-wrap items-center justify-center gap-3 text-sm">
            <span className="text-gray-300 font-mono text-xs bg-gray-800 px-3 py-1.5 rounded border border-gray-700">
              connections[0].ip → 185.220.101.5
            </span>
            <FlowArrow />
            <span className="text-gray-300 font-mono text-xs bg-gray-800 px-3 py-1.5 rounded border border-gray-700">
              TI_DB (D1)
            </span>
            <FlowArrow />
            <Badge variant="warning">KV Cache: MISS</Badge>
          </div>
        </Card.Body>
      </Card>

      {/* Alert card */}
      <Card className="border-red-500/30">
        <Card.Header className="border-red-500/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle size={16} className="text-red-400" />
              <span className="text-sm font-medium text-white">IoC Alert</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="danger">HIGH</Badge>
              <Badge variant="info">T1071.001</Badge>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          <p className="text-gray-300 text-sm">
            Outbound C2 traffic to known Emotet command server
          </p>
          <div className="flex items-center gap-2 mt-3 text-xs text-gray-500">
            <ExternalLink size={12} />
            <span>Source: feodo (Feodo Tracker)</span>
          </div>
        </Card.Body>
      </Card>

      {/* IoC table */}
      <div>
        <h3 className="text-sm font-medium text-gray-300 mb-3">All IoC Alerts from This Heartbeat</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left py-2 px-3 text-gray-500 font-medium text-xs uppercase tracking-wider">Type</th>
                <th className="text-left py-2 px-3 text-gray-500 font-medium text-xs uppercase tracking-wider">Value</th>
                <th className="text-left py-2 px-3 text-gray-500 font-medium text-xs uppercase tracking-wider">Threat Level</th>
                <th className="text-left py-2 px-3 text-gray-500 font-medium text-xs uppercase tracking-wider">ATT&CK</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/50">
              {IOC_ALERTS.map((alert, i) => (
                <tr key={i} className="hover:bg-dark-900/50 transition-colors">
                  <td className="py-2.5 px-3">
                    <Badge variant="outline">{alert.type}</Badge>
                  </td>
                  <td className="py-2.5 px-3 font-mono text-xs text-gray-300">{alert.value}</td>
                  <td className="py-2.5 px-3">
                    <Badge variant={alert.threatLevel === 'HIGH' ? 'danger' : 'warning'}>
                      {alert.threatLevel}
                    </Badge>
                  </td>
                  <td className="py-2.5 px-3">
                    <Badge variant="info">{alert.technique}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-white">
          Step 3: Full System Snapshot
        </h2>
        <p className="text-sm text-gray-400 mt-1">
          Every 1 hour, the agent sends a complete inventory of all packages, services, ports, and system info
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left — JSON */}
        <Card>
          <Card.Header>
            <div className="flex items-center gap-2">
              <Activity size={16} className="text-primary" />
              <span className="text-sm font-medium text-white">Snapshot Payload</span>
            </div>
          </Card.Header>
          <Card.Body className="p-0">
            <CodeBlock data={SNAPSHOT_PAYLOAD} />
          </Card.Body>
        </Card>

        {/* Right — Package Inventory + Services + Ports */}
        <div className="space-y-4">
          {/* Package Inventory */}
          <Card>
            <Card.Header>
              <div className="flex items-center gap-2">
                <Package size={16} className="text-primary" />
                <span className="text-sm font-medium text-white">Package Inventory</span>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="text-left py-2 px-3 text-gray-500 font-medium text-xs uppercase tracking-wider">Package</th>
                      <th className="text-left py-2 px-3 text-gray-500 font-medium text-xs uppercase tracking-wider">Ecosystem</th>
                      <th className="text-left py-2 px-3 text-gray-500 font-medium text-xs uppercase tracking-wider">Version</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700/50">
                    {SNAPSHOT_PAYLOAD.packages.map((pkg, i) => (
                      <tr key={i} className="hover:bg-dark-900/50 transition-colors">
                        <td className="py-2 px-3 font-mono text-xs text-gray-300">{pkg.name}</td>
                        <td className="py-2 px-3">
                          <Badge variant="secondary">{pkg.ecosystem}</Badge>
                        </td>
                        <td className="py-2 px-3 font-mono text-xs text-gray-300">{pkg.version}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>

          {/* Services & Ports */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Card>
              <Card.Header>
                <span className="text-sm font-medium text-white">Running Services</span>
              </Card.Header>
              <Card.Body>
                <div className="flex flex-wrap gap-2">
                  {SNAPSHOT_PAYLOAD.services.map((svc) => (
                    <Badge key={svc} variant="default">{svc}</Badge>
                  ))}
                </div>
              </Card.Body>
            </Card>
            <Card>
              <Card.Header>
                <span className="text-sm font-medium text-white">Open Ports</span>
              </Card.Header>
              <Card.Body>
                <div className="flex flex-wrap gap-2">
                  {SNAPSHOT_PAYLOAD.ports.map((p) => (
                    <Badge key={`${p.port}-${p.protocol}`} variant="secondary">
                      {p.port}/{p.protocol}
                    </Badge>
                  ))}
                </div>
              </Card.Body>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => {
    const changes = [
      { type: 'upgrade', package: 'nginx', from: '1.22.0', to: '1.24.0' },
      { type: 'install', package: 'flask', from: null, to: '3.0.0' },
    ];

    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-semibold text-white">
            Step 4: Snapshot Diff — Version Changes Detected
          </h2>
          <p className="text-sm text-gray-400 mt-1">
            Worker diffs against previous snapshot from KV cache and writes changes to the database
          </p>
        </div>

        {/* Before / After comparison */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="border-amber-500/20">
            <Card.Header className="border-amber-500/20">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-amber-400" />
                <span className="text-sm font-medium text-amber-300">Previous Snapshot</span>
              </div>
            </Card.Header>
            <Card.Body>
              <div className="space-y-2">
                {Object.entries(PREVIOUS_SNAPSHOT).map(([name, ver]) => (
                  <div key={name} className="flex items-center justify-between text-sm">
                    <span className="font-mono text-xs text-gray-400">{name}</span>
                    <span className="font-mono text-xs text-gray-300">{ver}</span>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>

          <Card className="border-emerald-500/20">
            <Card.Header className="border-emerald-500/20">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-400" />
                <span className="text-sm font-medium text-emerald-300">Current Snapshot</span>
              </div>
            </Card.Header>
            <Card.Body>
              <div className="space-y-2">
                {Object.entries(CURRENT_SNAPSHOT).map(([name, ver]) => (
                  <div key={name} className="flex items-center justify-between text-sm">
                    <span className="font-mono text-xs text-gray-400">{name}</span>
                    <span className="font-mono text-xs text-gray-300">{ver}</span>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        </div>

        {/* Change list */}
        <Card>
          <Card.Header>
            <div className="flex items-center gap-2">
              <GitCompare size={16} className="text-primary" />
              <span className="text-sm font-medium text-white">Detected Changes</span>
            </div>
          </Card.Header>
          <Card.Body className="space-y-3">
            {changes.map((ch, i) => (
              <div key={i} className="flex items-center gap-3 text-sm">
                <Badge variant={ch.type === 'upgrade' ? 'warning' : 'success'}>
                  {ch.type === 'upgrade' ? 'UPGRADE' : 'INSTALL'}
                </Badge>
                <span className="font-mono text-xs text-gray-300">{ch.package}</span>
                {ch.from ? (
                  <>
                    <span className="font-mono text-xs text-gray-500 line-through">{ch.from}</span>
                    <ArrowRight size={12} className="text-gray-600" />
                    <span className="font-mono text-xs text-emerald-400">{ch.to}</span>
                  </>
                ) : (
                  <span className="font-mono text-xs text-emerald-400">
                    {ch.to} (was not installed)
                  </span>
                )}
              </div>
            ))}
            <p className="text-xs text-gray-500 mt-2 pt-2 border-t border-gray-700/50">
              Version changes flagged for CVE cron pickup
            </p>
          </Card.Body>
        </Card>

        {/* Worker flow */}
        <Card>
          <Card.Body>
            <div className="flex flex-wrap items-center justify-center gap-3 text-sm">
              <Badge variant="info">Previous snapshot (KV)</Badge>
              <FlowArrow />
              <Badge variant="warning">DIFF engine</Badge>
              <FlowArrow />
              <Badge variant="default">version_changes table</Badge>
            </div>
            <div className="flex justify-center mt-3">
              <Badge variant="secondary">flag packages for CVE cron</Badge>
            </div>
          </Card.Body>
        </Card>
      </div>
    );
  };

  const renderStep5 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-white">
          Step 5: CVE Scan — NVD + OSV
        </h2>
        <p className="text-sm text-gray-400 mt-1">
          Cron job queries NVD and OSV databases every 6 hours for known vulnerabilities
        </p>
      </div>

      {/* Deduplication note */}
      <Card>
        <Card.Body className="flex items-center gap-3">
          <Shield size={16} className="text-primary" />
          <p className="text-sm text-gray-400">
            <span className="text-gray-200 font-medium">Deduplication:</span>{' '}
            3 unique (package, version) pairs → 3 API calls (not 6)
          </p>
        </Card.Body>
      </Card>

      {/* CVE Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {CVE_ALERTS.map((cve) => (
          <Card key={cve.id} className="border-gray-700 hover:border-gray-600 transition-colors">
            <Card.Header>
              <div className="flex items-center justify-between">
                <span className="text-sm font-mono font-semibold text-white">{cve.id}</span>
                <Badge variant={cve.severity === 'CRITICAL' ? 'danger' : 'warning'}>
                  {cve.severity}
                </Badge>
              </div>
            </Card.Header>
            <Card.Body className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">CVSS</span>
                <span className={`text-lg font-bold ${
                  cve.cvss >= 9 ? 'text-red-400' : cve.cvss >= 7 ? 'text-orange-400' : 'text-amber-400'
                }`}>
                  {cve.cvss.toFixed(1)}
                </span>
              </div>
              <p className="text-sm text-gray-300 leading-relaxed">{cve.description}</p>
              <div className="flex items-center gap-2 pt-2 border-t border-gray-700/50">
                <Package size={12} className="text-gray-500" />
                <span className="font-mono text-xs text-gray-400">{cve.package} {cve.version}</span>
                <Badge variant="secondary">{cve.ecosystem}</Badge>
              </div>
            </Card.Body>
          </Card>
        ))}
      </div>
    </div>
  );

  const renderStep6 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-white">
          Step 6: Platform Dashboard — Unified View
        </h2>
        <p className="text-sm text-gray-400 mt-1">
          All enrichment data consolidated into a single operational view
        </p>
      </div>

      {/* Risk Score */}
      <Card className="border-red-500/30">
        <Card.Body className="flex flex-col sm:flex-row items-center gap-6">
          <div className="text-center shrink-0">
            <div className="text-5xl font-bold text-red-400">87</div>
            <div className="text-xs text-gray-500 mt-1">/ 100</div>
          </div>
          <div className="flex-1">
            <Badge variant="danger" className="mb-2">CRITICAL</Badge>
            <p className="text-sm text-gray-400">
              Risk score is derived from <span className="text-gray-200">3 active IoC alerts</span> and{' '}
              <span className="text-gray-200">3 active CVE alerts</span>, weighted by severity.
            </p>
          </div>
        </Card.Body>
      </Card>

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Server} value="1" label="Total Assets" sublabel="prod-server-01" />
        <StatCard icon={AlertTriangle} value="3" label="Active IoC Alerts" sublabel="2 HIGH, 1 MEDIUM" />
        <StatCard icon={Shield} value="3" label="Active CVE Alerts" sublabel="2 CRITICAL, 1 MEDIUM" />
        <StatCard icon={FileSearch} value="6" label="Pending Review" sublabel="IoC + CVE combined" />
      </div>

      {/* Recent Alerts Timeline */}
      <Card>
        <Card.Header>
          <div className="flex items-center gap-2">
            <Activity size={16} className="text-primary" />
            <span className="text-sm font-medium text-white">Recent Alerts</span>
          </div>
        </Card.Header>
        <Card.Body>
          <div className="space-y-0 divide-y divide-gray-700/50">
            {[
              { time: '10:30 UTC', label: 'Emotet C2 traffic detected', badge: 'danger', badgeLabel: 'IoC' },
              { time: '10:30 UTC', label: 'Log4Shell vulnerability on log4j 2.14.1', badge: 'danger', badgeLabel: 'CVE' },
              { time: '10:30 UTC', label: 'OpenSSL vulnerability CVE-2022-0778', badge: 'danger', badgeLabel: 'CVE' },
              { time: '10:30 UTC', label: 'Requests cert bypass CVE-2023-32681', badge: 'warning', badgeLabel: 'CVE' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3 py-2.5">
                <span className="text-xs text-gray-500 w-16 shrink-0 font-mono">{item.time}</span>
                <Badge variant={item.badge} className="shrink-0">{item.badgeLabel}</Badge>
                <span className="text-sm text-gray-300">{item.label}</span>
              </div>
            ))}
          </div>
        </Card.Body>
      </Card>

      {/* Recommendations */}
      <Card className="border-amber-500/20">
        <Card.Header className="border-amber-500/20">
          <div className="flex items-center gap-2">
            <Shield size={16} className="text-amber-400" />
            <span className="text-sm font-medium text-amber-300">Remediation Recommendations</span>
          </div>
        </Card.Header>
        <Card.Body className="space-y-2">
          {[
            { label: 'Patch openssl to >=3.0.7', severity: 'CRITICAL' },
            { label: 'Upgrade log4j to >=2.17.0', severity: 'CRITICAL' },
            { label: 'Update requests to >=2.31.1', severity: 'MEDIUM' },
          ].map((rec, i) => (
            <div key={i} className="flex items-center gap-3 text-sm">
              <Badge variant={rec.severity === 'CRITICAL' ? 'danger' : 'warning'}>
                {rec.severity}
              </Badge>
              <span className="text-gray-300">{rec.label}</span>
            </div>
          ))}
        </Card.Body>
      </Card>
    </div>
  );

  // ==================================================================
  // PROGRESS INDICATOR
  // ==================================================================

  const renderProgress = () => (
    <div className="text-center mb-6">
      <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">
        Step {currentStep} of 6
      </p>
      <div className="flex items-center justify-center gap-2">
        {[1, 2, 3, 4, 5, 6].map((n) => (
          <button
            key={n}
            onClick={() => goToStep(n)}
            className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium transition-all duration-300 ${
              n === currentStep
                ? 'bg-primary text-white scale-110 shadow-lg shadow-primary/20'
                : n < currentStep
                ? 'bg-primary/20 text-primary border border-primary/30'
                : 'bg-gray-800 text-gray-500 border border-gray-700 hover:border-gray-600'
            }`}
            aria-label={`Go to step ${n}`}
          >
            {n}
          </button>
        ))}
      </div>
    </div>
  );

  // ==================================================================
  // NAVIGATION BAR
  // ==================================================================

  const renderNavigation = () => (
    <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-700/50">
      <Button
        variant="outline"
        size="sm"
        onClick={handlePrev}
        disabled={currentStep === 1}
        className="disabled:opacity-30 disabled:cursor-not-allowed"
      >
        <ChevronLeft size={16} className="mr-1" />
        Previous Step
      </Button>

      {/* Step dots (always visible in nav) */}
      <div className="hidden sm:flex items-center gap-1.5">
        {[1, 2, 3, 4, 5, 6].map((n) => (
          <button
            key={n}
            onClick={() => goToStep(n)}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              n === currentStep
                ? 'bg-primary w-5'
                : n < currentStep
                ? 'bg-primary/40'
                : 'bg-gray-600 hover:bg-gray-500'
            }`}
            aria-label={`Go to step ${n}`}
          />
        ))}
      </div>

      <Button
        variant={currentStep === 6 ? 'danger' : 'primary'}
        size="sm"
        onClick={handleNext}
      >
        {currentStep === 6 ? (
          <>
            <RotateCcw size={16} className="mr-1.5" />
            Restart Demo
          </>
        ) : (
          <>
            Next Step
            <ChevronRight size={16} className="ml-1" />
          </>
        )}
      </Button>
    </div>
  );

  // ==================================================================
  // MAIN RENDER
  // ==================================================================

  return (
    <PageContainer
      title=""
      actions={
        <Badge variant="info" className="text-xs">
          PROTOTYPE — DEMO DATA
        </Badge>
      }
    >
      {/* Keyboard hint */}
      <p className="text-center text-xs text-gray-600 mb-4">
        Press <kbd className="px-1.5 py-0.5 bg-gray-800 rounded text-gray-400 font-mono text-[10px] border border-gray-700">Enter</kbd> or{' '}
        <kbd className="px-1.5 py-0.5 bg-gray-800 rounded text-gray-400 font-mono text-[10px] border border-gray-700">Space</kbd> to advance
      </p>

      {/* Progress */}
      {renderProgress()}

      {/* Animated step content */}
      <div className={`transition-all duration-300 ${stepTransition}`}>
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
        {currentStep === 4 && renderStep4()}
        {currentStep === 5 && renderStep5()}
        {currentStep === 6 && renderStep6()}
      </div>

      {/* Navigation */}
      {renderNavigation()}
    </PageContainer>
  );
};

export default EnrichmentFlowDemo;
